<template>
  <div>
    <div class="p-4 sm:p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>
    <div class="p-4 sm:p-6 pb-0">
      <div class="flex items-center justify-between mb-4">
        <label class="block text-sm font-medium">{{ isOrphan ? $t('gm.orphan_picking') : $t('core.order_details') }}</label>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div v-if="isOrphan">
          <label class="text-sm text-gray-500 font-medium mb-2 block">{{ $t('core.customer') }} *</label>
          <v-select
              v-model="customer"
              class="w-full bg-white text-gray-500 rounded-md focus:ring focus:ring-blue-200 focus:border-blue-500" 
              :options="customers ?? []"
              label="CompanyName"
              placeholder="Digita nome o codice cliente..."
              :clearable="true"
              :filter-by="(option, label, search) => {
                const q = search.toLowerCase();
                return (option.CompanyName || '').toLowerCase().includes(q)
                    || (option.CustSupp || '').toLowerCase().includes(q);
              }"
            >
              <template #option="option">
                <span class="font-medium">{{ option.CompanyName }}</span>
                <span class="text-gray-400 text-sm ml-2">({{ option.CustSupp }})</span>
              </template>
              <template #selected-option="option">
                <span>{{ option.CompanyName }} <span class="text-gray-400 text-sm">({{ option.CustSupp }})</span></span>
              </template>
              <template #no-options>
                Nessun cliente trovato.
              </template>
            </v-select>
        </div>

        <div :class="isOrphan ? '' : 'md:col-span-2'">
          <label class="text-sm text-gray-500 font-medium mb-2 block">{{ $t('core.storage') }} *</label>
          <v-select
              v-model="storage"
              class="w-full bg-white text-gray-500 rounded-md focus:ring focus:ring-blue-200 focus:border-blue-500" 
              :options="storages ?? []"
              label="Description"
              placeholder="Seleziona deposito di origine..."
              :clearable="true"
              :filter-by="(option, label, search) => {
                const q = search.toLowerCase();
                return (option.Description || '').toLowerCase().includes(q)
                    || (option.Storage || '').toLowerCase().includes(q);
              }"
            >
              <template #option="option">
                <span class="font-medium">{{ option.Storage }}</span>
                <span class="text-gray-400 text-sm ml-2">- {{ option.Description }}</span>
              </template>
              <template #selected-option="option">
                <span>{{ option.Storage }} <span class="text-gray-400 text-sm">- {{ option.Description }}</span></span>
              </template>
              <template #no-options>
                Nessun deposito trovato.
              </template>
            </v-select>
        </div>
      </div>

      <div class="mb-6">
        <div class="relative">
          <input
            ref="barcodeInput"
            v-model="barcodeSearch"
            type="text"
            :placeholder="!selectedStorageCode ? 'Seleziona prima un deposito...' : ($t('core.barcode_lot_search') + '...')"
            :disabled="!selectedStorageCode"
            class="w-full p-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-sm sm:text-base"
            @keyup.enter="searchItemfromBarcode()"
            @keydown.enter.prevent
            autofocus
          
          />
          <div class="absolute right-3 top-3 text-gray-400">
            <i class="fa fa-search"></i>
          </div>
        </div>
      </div>

      <template v-if="!isOrphan">
        <div class="flex items-center justify-between mb-4">
          <label class="block text-sm font-medium mb-2">{{ $t('core.items_list') }}</label>
        </div>

        <PageNormalTable
          :headers="itemsHeaders"
          :items="items"
          :item-value="item => `${item.SaleOrdId}-${item.Position}`"
          hide-default-footer
          :row-props="getRowProps"
          :mobile-filter-exclusions="['Qty', 'DeliveredQty', 'AlreadyInPicking', 'CommitQty', 'actions']"
        >
          <template #item.actions="{ item }">
            <button
              type="button"
              class="text-white bg-blue-500 hover:bg-blue-600 font-medium rounded-md text-xs px-3 py-1.5"
              @click.stop="searchItemfromBarcode(item)"
            >
              {{ $t('actions.add') }}
            </button>
          </template>
        </PageNormalTable>
      </template>

      <div v-if="pickingGroups.length > 0" class="mt-6">
        <div class="flex items-center justify-between mb-4">
          <label class="block text-sm font-medium mb-2">{{ $t('gm.open_picking_overview') }}</label>
        </div>
        <div v-for="group in pickingGroups" :key="group.SaleDocId" class="mb-6">
          <div class="text-sm text-gray-600 mb-2">
            {{ $t('gm.open_picking_overview_desc', { docno: group.DocNo, count: group.rows.length, other: group.rows.filter(r => !r.SameOrder).length }) }}
            <span v-if="group.StoragePhase1" class="ml-2 text-xs font-semibold px-2 py-0.5 rounded bg-blue-100 text-blue-800">{{ $t('core.storage') }}: {{ group.StoragePhase1 }}</span>
          </div>
          <PageNormalTable
            :headers="pickingGroupHeaders"
            :items="group.rows"
            :item-value="(item, idx) => `${item.SaleOrdId}-${item.SaleOrdPos}-${idx}`"
            hide-default-footer
            :row-props="getPickingGroupRowProps"
            :mobile-filter-exclusions="['Qty', 'BaseUoM']"
          >
            <template #item.SaleOrdNo="{ item }">
              <span>{{ item.SaleOrdNo }}</span>
              <span v-if="!item.SameOrder" class="ml-2 inline-block px-2 py-0.5 text-xs rounded bg-yellow-200 text-yellow-800">{{ $t('gm.other_order') }}</span>
            </template>
            <template #item.Lot="{ item }">
              <span>{{ item.Lot || '-' }}</span>
            </template>
          </PageNormalTable>
        </div>
      </div>

      <div v-if="freeItems.length > 0" class="mt-6">
        <div class="flex items-center justify-between mb-4">
          <label class="block text-sm font-medium mb-2">{{ $t('gm.picking_rows') }}</label>
        </div>
        <PageNormalTable
          :headers="freeItemsHeaders"
          :items="freeItems"
          :item-value="(item, idx) => `${item.Item}-${item.Lot}-${idx}`"
          hide-default-footer
          :mobile-filter-exclusions="['Quantity', 'UoM', 'Price']"
        >
          <template #item.Storage="{ item }">
            <span>{{ item.Storage || '-' }}</span>
          </template>
          <template #item.Lot="{ item }">
            <span>{{ item.Lot || '-' }}</span>
          </template>
          <template #item.Price="{ item }">
            <span>{{ item.Price != null ? item.Price : '-' }}</span>
          </template>
          <template #item.actions="{ item, index }">
            <button @click="removeFreeItem(item, index)" class="text-red-500 hover:text-red-700 p-1" :title="$t('actions.delete')">
              <i class="fa fa-times"></i>
            </button>
          </template>
        </PageNormalTable>
      </div>

      <div class="pb-24"></div>
    </div>

    <div v-if="!isOrphan" class="fixed left-0 right-0 bottom-0 flex justify-center items-center pointer-events-none z-40">
      <div class="flex gap-2 pointer-events-auto bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-lg w-full px-4 py-3 justify-center">
        <button
          v-if="!hasQtyToConfirm"
          class="w-full sm:w-auto text-white bg-blue-400 cursor-not-allowed font-medium rounded-lg text-sm px-6 py-2.5 text-center"
        >
          {{ $t('gm.confirm_picking') }}
        </button>
        <button
          v-else
          class="w-full sm:w-auto text-white bg-blue-500 hover:bg-blue-600 font-medium rounded-lg text-sm px-6 py-2.5 text-center shadow transition-colors"
          :disabled="confirming"
          @click="confermaPicking"
        >
          {{ $t('gm.confirm_picking') }}
        </button>
      </div>
    </div>

    <div v-if="isOrphan" class="fixed left-0 right-0 bottom-0 flex justify-center items-center pointer-events-none z-40">
      <div class="flex gap-2 pointer-events-auto bg-white/95 backdrop-blur-sm border-t border-gray-200 shadow-lg w-full px-4 py-3 justify-center">
        <button
          v-if="!canCreateOrphanPicking"
          class="w-full sm:w-auto text-white bg-blue-400 cursor-not-allowed font-medium rounded-lg text-sm px-6 py-2.5 text-center"
        >
          {{ $t('gm.create_picking') }}
        </button>
        <button
          v-else
          class="w-full sm:w-auto text-white bg-blue-500 hover:bg-blue-600 font-medium rounded-lg text-sm px-6 py-2.5 text-center shadow transition-colors"
          :disabled="confirming"
          @click="creaPickingOrfano"
        >
          {{ $t('gm.create_picking') }}
        </button>
      </div>
    </div>

    <ConfirmationModal :show="showSelectModal" @close="showSelectModal = false">
      <template #title>{{ $t('gm.select_item_variant') }}</template>
      <template #content>
        <div class="max-h-80 overflow-y-auto divide-y divide-gray-200 border border-gray-200 rounded-md">
          <button
            v-for="(result, idx) in selectionResults"
            :key="idx"
            class="w-full text-left px-3 py-2 flex flex-col border border-gray-300 transition-colors"
            :class="{
              'bg-red-50 text-red-700 border-red-200 opacity-60 cursor-not-allowed': Number(result.FinalOnHand) === 0,
              'hover:bg-gray-100 cursor-pointer': Number(result.FinalOnHand) > 0
            }"
            :disabled="Number(result.FinalOnHand) === 0"
            @click="Number(result.FinalOnHand) > 0 && selectResult(result)"
          >
            <span class="font-medium">
              {{ result.Item }} - {{ result.Description }}
              <span v-if="Number(result.FinalOnHand) === 0" class="ml-2 text-xs font-semibold uppercase">(Out of Stock)</span>
            </span>
            <span class="text-sm" :class="Number(result.FinalOnHand) === 0 ? 'text-red-500' : 'text-gray-500'">
              {{ $t('core.storage') }}: {{ result.Storage || '-' }}
              &nbsp;|&nbsp;
              {{ $t('core.lot') }}: {{ result.Lot || '-' }}
              &nbsp;|&nbsp;
              {{ $t('core.qty') }}: {{ result.FinalOnHand }}
            </span>
          </button>
        </div>
      </template>
      <template #footer>
        <SecondaryButton @click="showSelectModal = false">
          {{ $t('actions.cancel') }}
        </SecondaryButton>
      </template>
    </ConfirmationModal>

    <ConfirmationModal :show="showQtyModal" @close="showQtyModal = false">
      <template #title>{{ $t('gm.insert_qty') }}</template>
      <template #content>
        <div>
          {{ $t('gm.insert_qty_for') }} {{ pendingItem?.Item }}
          <br>
          Quantità Massima disponibile: {{ maxQty }}
          <br v-if="pendingMode === 'free' && pendingItem?.Price != null">
          <span v-if="pendingMode === 'free' && pendingItem?.Price != null">
            {{ $t('core.price') }}: {{ pendingItem.Price }}
            <span v-if="pendingItem.DiscountFormula"> ({{ $t('core.discount') }}: {{ pendingItem.DiscountFormula }})</span>
          </span>
          <input 
            v-model.number="tmpQty" 
            onfocus="this.select()" 
            class="w-full p-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mt-2" 
            type="number" 
            :max="pendingItem?.FinalOnHand"
            @input="validateMaxQty"
            >
        </div>
      </template>
      <template #footer>
        <SecondaryButton @click="showQtyModal = false">
          {{ $t('actions.cancel') }}
        </SecondaryButton>
        <PrimaryButton class="ms-3" @click="confirmQty">
          {{ $t('actions.save') }}
        </PrimaryButton>
      </template>
    </ConfirmationModal>

    <ToastNotification
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      @update:show="toast.show = $event"
    />

    <Loader :visible="confirming || searching" :text="searching ? $t('core.searching') : message" />
  </div>
</template>

<script>
import { Link, router } from '@inertiajs/vue3';
import axios from 'axios';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import PageNormalTable from '@/Components/PageNormalTable.vue';
import ConfirmationModal from '@/Components/ConfirmationModal.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import SecondaryButton from '@/Components/SecondaryButton.vue';
import ToastNotification from '@/Components/ToastNotification.vue';
import Loader from '@/Components/Loader.vue';

export default {
  props: ['items', 'headers', 'isOrphan', 'pickingGroups', 'customers', 'storages'],
  components: {
    Link,
    BreadCrumbs,
    PageNormalTable,
    ConfirmationModal,
    PrimaryButton,
    SecondaryButton,
    Loader,
    ToastNotification
  },
  data() {
    return {
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: this.$t('modules.gestione-magazzino'), href: route('modules.home', 'gestione-magazzino') },
        { name: this.$t('gm.list_client_orders'), href: route('modules.gm.pickinglist') }
      ],
      barcodeSearch: '',
      freeItems: [],
      showQtyModal: false,
      showSelectModal: false,
      selectionResults: [],
      pendingItem: null,
      pendingMode: null, // 'order' | 'free'
      pendingOrderItem: null,
      tmpQty: 1,
      toast: {
        show: false,
        message: '',
        type: 'success'
      },
      confirming: false,
      searching: false,
      maxQty: null,
      customer: null,
      storage: null,
      message: '',
    };
  },
  computed: {
    itemsHeaders() {
      return [
        ...(this.headers ?? []),
        { title: '', key: 'actions', value: 'actions', align: 'center', sortable: false, filterable: false }
      ];
    },
    selectedStorageCode() {
      return this.storage?.Storage ?? (typeof this.storage === 'string' ? this.storage : '');
    },
    hasQtyToConfirm() {
      return this.items.some(item => Number(item.CommitQty) > 0) && !!this.selectedStorageCode;
    },
    canCreateOrphanPicking() {
      return this.freeItems.length > 0 && !!this.customer && !!this.selectedStorageCode;
    },
    pickingGroupHeaders() {
      return [
        { title: this.$t('core.order'), key: 'SaleOrdNo', value: 'SaleOrdNo', align: 'start' },
        { title: this.$t('core.item'), key: 'Item', value: 'Item', align: 'start' },
        { title: this.$t('core.description'), key: 'Description', value: 'Description', align: 'start' },
        { title: this.$t('core.lot'), key: 'Lot', value: 'Lot', align: 'start' },
        { title: this.$t('core.qty'), key: 'Qty', value: 'Qty', align: 'end' },
        { title: this.$t('core.uom'), key: 'BaseUoM', value: 'BaseUoM', align: 'end' }
      ];
    },
    freeItemsHeaders() {
      return [
        { title: this.$t('core.item'), key: 'Item', value: 'Item', align: 'start' },
        { title: this.$t('core.description'), key: 'Description', value: 'Description', align: 'start' },
        { title: this.$t('core.storage'), key: 'Storage', value: 'Storage', align: 'start' },
        { title: this.$t('core.lot'), key: 'Lot', value: 'Lot', align: 'start' },
        { title: this.$t('core.qty'), key: 'Quantity', value: 'Quantity', align: 'end' },
        { title: this.$t('core.uom'), key: 'UoM', value: 'UoM', align: 'end' },
        { title: this.$t('core.price'), key: 'Price', value: 'Price', align: 'end' },
        { title: '', key: 'actions', value: 'actions', align: 'center', sortable: false, filterable: false }
      ];
    }
  },
  methods: {
    showToast(message, type = 'success') {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;
    },
    searchItemfromBarcode(item = null) {
      if (!this.selectedStorageCode) {
        this.showToast('Seleziona prima un deposito di origine', 'error');
        return;
      }

      let _this = this;

      if (item) {
        this.searching = true;
        axios.post(route('modules.gm.pickingcercaItem'), {
          item: item.Item,
          storage: this.selectedStorageCode
        }).then((response) => {
          const results = response.data;
          if (!results || results.length === 0) {
            _this.showToast(_this.$t('core.item_not_found'), 'error');
            return;
          }
          _this.barcodeSearch = '';
          if (results.length >= 1) {
            _this.selectionResults = results;
            _this.showSelectModal = true;
            return;
          }
          _this.pendingItem = results[0];
          _this.pendingMode = 'free';
          _this.tmpQty = 1;
          _this.showQtyModal = true;
          if (_this.isOrphan) {
            _this.fetchItemPrice(_this.pendingItem);
          }
        }).catch((error) => {
          const message = error.response?.data?.message || _this.$t('core.item_not_found');
          _this.showToast(message, 'error');
        }).finally(() => {
          _this.searching = false;
        });
      } else {
        if (!this.barcodeSearch || !this.barcodeSearch.trim()) return;

        this.searching = true;
        axios.post(route('modules.gm.pickingcercabarcode'), {
          barcode: this.barcodeSearch,
          storage: this.selectedStorageCode
        }).then((response) => {
          const results = response.data;

          if (!results || results.length === 0) {
            _this.showToast(_this.$t('core.item_not_found'), 'error');
            return;
          }

          _this.barcodeSearch = '';


          if (results.length >= 1) {
            _this.selectionResults = results;
            _this.showSelectModal = true;
            return;
          }

          _this.pendingItem = results[0];
          _this.pendingMode = 'free';
          _this.tmpQty = 1;
          _this.showQtyModal = true;

          if (_this.isOrphan) {
            _this.fetchItemPrice(_this.pendingItem);
          }
        }).catch((error) => {
          const message = error.response?.data?.message || _this.$t('core.item_not_found');
          _this.showToast(message, 'error');
        }).finally(() => {
          _this.searching = false;
        });
      }
    },

    
    findOrderItem(itemCode) {
      const code = String(itemCode || '').trim().toUpperCase();
      if (!code) return null;

      return this.items.find(it => String(it.Item || '').trim().toUpperCase() === code
          && (Number(it.Qty) - Number(it.AlreadyInPicking || 0) - Number(it.CommitQty || 0)) > 0)
        || this.items.find(it => String(it.Item || '').trim().toUpperCase() === code)
        || null;
    },
    selectResult(result) {
      this.showSelectModal = false;
      this.selectionResults = [];
      this.pendingItem = result;

      if (!this.isOrphan) {
        const orderItem = this.findOrderItem(result.Item);
        if (!orderItem) {
          this.showToast(this.$t('gm.item_found_not_in_order', { item: result.Item, description: result.Description }), 'error');
          this.pendingItem = null;
          return;
        }

        this.pendingMode = 'order';
        this.pendingOrderItem = orderItem;
        const remaining = Math.max(Number(orderItem.Qty) - Number(orderItem.AlreadyInPicking || 0) - Number(orderItem.CommitQty || 0), 0);
        this.maxQty = Math.min(Number(result.FinalOnHand ?? remaining), remaining);
      } else {
        this.pendingMode = 'free';
        this.maxQty = this.pendingItem.FinalOnHand;
      }

      this.tmpQty = Math.min(this.tmpQty, this.maxQty);
      this.validateMaxQty();
      this.$nextTick(() => {
        const input = this.$el.querySelector('input[type="number"]');
        if (input) input.setAttribute('max', this.maxQty);
      });
      this.showQtyModal = true;

      if (this.isOrphan) {
        this.fetchItemPrice(result);
      }
    },
    fetchItemPrice(item) {
      let _this = this;
      axios.post(route('modules.gm.cercaprezzitem'), {
        item: item.Item,
        customer: this.customer?.CustSupp ?? ''
      }).then(response => {
        const priceData = response.data?.[0] ?? null;
        if (!priceData) return;

        item.Price = priceData.Price ?? null;
        item.DiscountFormula = priceData.DiscountFormula ?? '';
        item.PriceList = priceData.PriceList ?? '';

        if (_this.pendingItem === item) {
          _this.pendingItem = { ..._this.pendingItem };
        }
      }).catch(() => {});
    },
    isFullyInPicking(item) {
      return Number(item.AlreadyInPicking) >= Number(item.Qty);
    },
    getRowProps(data) {
      const item = data?.item ?? data;
      const isFully = this.isFullyInPicking(item);
      const isPart = Number(item.AlreadyInPicking || item.alreadyInPicking || 0) > 0;
      if (isFully) {
        return { class: 'bg-orange-100 hover:bg-orange-200' };
      }
      if (isPart) {
        return { class: 'bg-green-100 hover:bg-green-200' };
      }
      return {};
    },
    getPickingGroupRowProps(data) {
      const item = data?.item ?? data;
      if (!item.SameOrder) {
        return { class: 'bg-yellow-50 hover:bg-yellow-100' };
      }
      return {};
    },
    removeFreeItem(item, index) {
      const idx = this.freeItems.indexOf(item);
      if (idx !== -1) {
        this.freeItems.splice(idx, 1);
      } else if (index !== undefined && index >= 0) {
        this.freeItems.splice(index, 1);
      }
    },
    confermaPicking() {
      if (this.confirming || !this.hasQtyToConfirm) return;

      let _this = this;
      const payload = {
        Storage: this.selectedStorageCode,
        items: this.items
          .filter(item => Number(item.CommitQty) > 0)
          .map(item => ({
            SaleOrdId: item.SaleOrdId,
            Position: item.Position,
            CommitQty: item.CommitQty,
            Lot: item.Lot || ''
          })),
        saleOrdIds: [...new Set(this.items.map(item => item.SaleOrdId))]
      };
      this.message = this.$t('actions.confirming_picking');
      this.confirming = true;
      axios.post(route('modules.gm.pickingconferma'), payload).then((response) => {
        _this.showToast(_this.$t('gm.picking_confirmed'), 'success');
        router.visit(route('modules.gm.pickinglist'));
      }).catch((error) => {
        const message = error.response?.data?.message || _this.$t('gm.picking_confirm_error');
        _this.showToast(message, 'error');
      }).finally(() => {
        _this.confirming = false;
      });
    },
    creaPickingOrfano() {
      if (this.confirming || !this.canCreateOrphanPicking) return;

      let _this = this;
      const payload = {
        CompanyName: this.customer.CompanyName,
        CustSupp: this.customer.CustSupp,
        Storage: this.selectedStorageCode,
        freeItems: this.freeItems.map(item => ({
          Item: item.Item,
          Description: item.Description,
          Quantity: item.Quantity,
          UoM: item.UoM,
          Lot: item.Lot,
          Storage: item.Storage || _this.selectedStorageCode,
          Price: item.Price,
          DiscountFormula: item.DiscountFormula
        }))
      };

      this.confirming = true;
      axios.post(route('modules.gm.pickingconferma'), payload).then((response) => {
        _this.showToast(_this.$t('gm.picking_confirmed'), 'success');
        router.visit(route('modules.gm.pickinglist'));
      }).catch((error) => {
        const message = error.response?.data?.message || _this.$t('gm.picking_confirm_error');
        _this.showToast(message, 'error');
      }).finally(() => {
        _this.confirming = false;
      });
    },
    removePickingRow(saleDocId, row) {
      let _this = this;
      axios.post(route('modules.gm.pickingremoverow'), {
        saleDocId,
        saleOrdId: row.SaleOrdId,
        saleOrdPos: row.SaleOrdPos
      }).then(() => {
        const group = _this.pickingGroups.find(g => g.SaleDocId === saleDocId);
        if (group) {
          const idx = group.rows.indexOf(row);
          if (idx !== -1) group.rows.splice(idx, 1);
        }
        _this.showToast(_this.$t('core.confirm'), 'success');
      }).catch(() => {
        _this.showToast(_this.$t('core.error'), 'error');
      });
    },
    validateMaxQty() {
      if (this.tmpQty > this.maxQty) {
        this.tmpQty = this.maxQty;
      }
    },
    confirmQty() {
      if (this.pendingMode === 'order') {
        this.validateMaxQty();
        if (this.pendingOrderItem) {
          this.pendingOrderItem.CommitQty = Number(this.pendingOrderItem.CommitQty || 0) + Number(this.tmpQty);
          if (this.pendingItem?.Lot) {
            this.pendingOrderItem.Lot = this.pendingItem.Lot;
          }
        }
      } else if (this.pendingMode === 'free') {
        this.validateMaxQty();
        const itemStorage = this.pendingItem.Storage || this.selectedStorageCode || '';
        const existing = this.freeItems.find(x =>
          x.Item == this.pendingItem.Item &&
          (x.Lot || '') == (this.pendingItem.Lot || '') &&
          (x.Storage || '') == itemStorage
        );
        if (existing) {
          existing.Quantity = Number(existing.Quantity) + Number(this.tmpQty);
        } else {
          this.freeItems.push({
            Item: this.pendingItem.Item,
            Description: this.pendingItem.Description,
            Quantity: this.tmpQty,
            UoM: this.pendingItem.UoM || this.pendingItem.BaseUoM || '',
            Lot: this.pendingItem.Lot || '',
            Storage: itemStorage,
            Price: this.pendingItem.Price ?? null,
            DiscountFormula: this.pendingItem.DiscountFormula || '',
            PriceList: this.pendingItem.PriceList || ''
          });
        }
      }
      this.showQtyModal = false;
      this.pendingItem = null;
      this.pendingMode = null;
      this.pendingOrderItem = null;
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.barcodeInput?.focus();
    });
  }
};
</script>
